export const AVATAR_PRESET_KEYS = Array.from({ length: 9 }, (_, index) => `avatar${index + 1}`);

export type AvatarSettings = {
  selectedAvatarId: string;
  discordAvatarUrl: string;
};

const AVATAR_SETTINGS_KEY = 'poker_avatar_settings_v1';
const DEFAULT_SETTINGS: AvatarSettings = {
  selectedAvatarId: 'discord',
  discordAvatarUrl: '',
};

const normalizeSelectedAvatarId = (value: unknown): string => {
  if (value === 'discord') return 'discord';
  if (typeof value === 'string' && AVATAR_PRESET_KEYS.includes(value)) return value;
  return DEFAULT_SETTINGS.selectedAvatarId;
};

const normalizeDiscordAvatarUrl = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  return value.trim();
};

const computeDiscordDefaultAvatarIndex = (userId: string, discriminator?: string | null): number => {
  const normalizedDiscriminator = typeof discriminator === 'string' ? discriminator.trim() : '';
  if (normalizedDiscriminator && normalizedDiscriminator !== '0') {
    const parsed = Number.parseInt(normalizedDiscriminator, 10);
    if (Number.isFinite(parsed)) {
      return parsed % 5;
    }
  }

  try {
    const idValue = BigInt(userId.trim());
    return Number((idValue >> 22n) % 6n);
  } catch {
    return 0;
  }
};

export const buildDiscordAvatarUrl = (
  userId: string,
  avatarHash?: string | null,
  discriminator?: string | null,
): string => {
  const trimmedUserId = userId.trim();
  if (!trimmedUserId) return '';
  if (avatarHash) {
    if (avatarHash.startsWith('http://') || avatarHash.startsWith('https://')) {
      return avatarHash;
    }
    return `https://cdn.discordapp.com/avatars/${trimmedUserId}/${avatarHash}.png?size=128`;
  }
  const fallbackIndex = computeDiscordDefaultAvatarIndex(trimmedUserId, discriminator);
  return `https://cdn.discordapp.com/embed/avatars/${fallbackIndex}.png`;
};

export const loadAvatarSettings = (): AvatarSettings => {
  try {
    const stored = localStorage.getItem(AVATAR_SETTINGS_KEY);
    if (!stored) return { ...DEFAULT_SETTINGS };
    const parsed = JSON.parse(stored) as Partial<AvatarSettings>;
    return {
      selectedAvatarId: normalizeSelectedAvatarId(parsed.selectedAvatarId),
      discordAvatarUrl: normalizeDiscordAvatarUrl(parsed.discordAvatarUrl),
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
};

export const saveAvatarSettings = (settings: AvatarSettings): void => {
  const normalized: AvatarSettings = {
    selectedAvatarId: normalizeSelectedAvatarId(settings.selectedAvatarId),
    discordAvatarUrl: normalizeDiscordAvatarUrl(settings.discordAvatarUrl),
  };
  try {
    localStorage.setItem(AVATAR_SETTINGS_KEY, JSON.stringify(normalized));
  } catch {
    // Persisting settings is best-effort; ignore storage errors
  }
};

export const getSelectedAvatarValue = (settings: AvatarSettings): string => {
  if (settings.selectedAvatarId === 'discord') {
    return settings.discordAvatarUrl || AVATAR_PRESET_KEYS[0];
  }
  if (AVATAR_PRESET_KEYS.includes(settings.selectedAvatarId)) {
    return settings.selectedAvatarId;
  }
  return AVATAR_PRESET_KEYS[0];
};
